import { ComponentType, PropsWithChildren } from 'react';
import { generatePath, Navigate, useLocation, useMatch } from 'react-router-dom';

import { useDispatch } from 'react-redux';

import { useCurrentSupportUser } from '@/features/dashboard-users/hooks/useCurrentSupportUser';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import { routeStore } from '@/store/configuration';
import { logout } from '@/store/actions/auth.actions';

import { useAccesControl } from '@/hooks/useAccessControl';
import { useRoutepaths } from '@/router/hooks/useRoutePaths';

const RequireAuth = ({ children }: PropsWithChildren) => {
  const dispatch = useDispatch();
  const { isAuthenticated, has2FAEnabled, isSSO, accessTokenDecoded } = useCurrentSupportUser();
  const config = useDashboardConfig();
  const PathRoute = useRoutepaths();

  const { pathname } = useLocation();
  const { hasAccessToRoute } = useAccesControl();

  const isAllowed =
    hasAccessToRoute(pathname) && (has2FAEnabled && !isSSO ? accessTokenDecoded?.secureAuthentication : true);

  const isPassphraseRoute = useMatch(PathRoute.auth.passphrase);

  if (!(routeStore.supportUserPassphrase || isPassphraseRoute) && config?.api.encryption)
    return <Navigate to={generatePath(PathRoute.auth.root, { '*': PathRoute.auth.passphrase })} />;

  if (isAuthenticated && isAllowed) return children;

  if (!isAllowed) {
    return <Navigate to={generatePath(PathRoute.auth.root, { '*': PathRoute.auth.role })} />;
  }

  dispatch(logout());
  return <Navigate to={generatePath(PathRoute.auth.root, { '*': PathRoute.auth.signIn })} />;
};

export const withAuth = (WrappedComponent: ComponentType) => {
  return (
    <RequireAuth>
      <WrappedComponent />
    </RequireAuth>
  );
};
